import { Component, h, Prop, Event, EventEmitter, Build } from '@stencil/core';

/**
 * Single radio button form control with a text label.
 * Part of a radio group where only one option can be selected at a time.
 * Supports disabled and required states; emits a change event on selection.
 *
 * @example
 *   <me-radio name="plan" value="pro" label="Professional Plan"></me-radio>
 */
@Component({
  tag: 'me-radio',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Radio {
  /**
   * The text label that appears next to the radio button
   * @default ''
   */
  @Prop() label: string = '';

  /**
   * The value associated with this radio button
   */
  @Prop() value: string = '';

  /**
   * The currently selected value in the radio group
   */
  @Prop({ mutable: true }) checked: boolean = false;

  /**
   * Whether the radio button is disabled
   * @default false
   */
  @Prop() disabled: boolean = false;

  /**
   * The name of the radio group
   */
  @Prop() name: string = '';

  /**
   * Whether the radio button is required in a form
   * @default false
   */
  @Prop() required: boolean = false;

  /**
   * Error state for radio (set by form handler)
   */
  @Prop() error?: string;

  /**
   * Error message for radio (set by form handler)
   */
  @Prop() errorMessage?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop() errorType?: 'required' | 'invalid';

  /**
   * Emitted when the radio button is selected
   * @event valueChange
   */
  @Event() valueChange: EventEmitter<string>;

  @Event() clearFieldError: EventEmitter<string>;

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = (this as any).hostEl?.parentElement || null;
    while (parent) {
      if (parent.tagName && parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }

  private handleChange = (event: Event) => {
    const radio = event.target as HTMLInputElement;
    if (radio.checked) {
      this.checked = true;
      this.valueChange.emit(this.value);
      this.clearFieldError.emit(this.getResolvedName());
      (this as any).hostEl?.removeAttribute('error');
      (this as any).hostEl?.removeAttribute('error-message');
      (this as any).hostEl?.removeAttribute('error-type');
    }
  };

  private isClickOnLink(target: EventTarget | null): boolean {
    let el = target as HTMLElement | null;
    while (el) {
      if (el.tagName === 'A') return true;
      el = el.parentElement;
    }
    return false;
  }

  private handleClick = (event: MouseEvent) => {
    if (this.disabled) return;
    // If click was on a link, do not toggle radio
    if (this.isClickOnLink(event.target)) {
      return;
    }
    const label = event.currentTarget as HTMLLabelElement;
    const input = label.querySelector('input') as HTMLInputElement;
    if (input && !input.checked) {
      input.checked = true;
      this.checked = true;
      this.valueChange.emit(this.value);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) return;
    // If focus is on a link and Enter is pressed, do not toggle radio
    const active = Build.isBrowser ? document.activeElement : null;
    if (active && active.tagName === 'A' && event.key === 'Enter') {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const label = event.currentTarget as HTMLLabelElement;
      const input = label.querySelector('input') as HTMLInputElement;
      if (input && !input.checked) {
        input.checked = true;
        this.checked = true;
        this.valueChange.emit(this.value);
      }
    }
  };

  private handleFocus = () => {
    this.clearFieldError.emit(this.getResolvedName());
  };

  render() {
    const hasError = this.error === 'true';
    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      if (this.errorType === 'required') errorText = this.requiredMessage || 'This field is required';
      else if (this.errorType === 'invalid') errorText = this.invalidMessage || 'Invalid value';
      else errorText = 'This field is invalid';
    }
    const resolvedName = this.getResolvedName();
    return (
      <label
        class={{
          'radio': true,
          'radio--checked': this.checked,
          'radio--disabled': this.disabled,
          'radio--error': hasError,
        }}
        onClick={this.handleClick}
        onKeyDown={this.handleKeyDown}
        role="presentation"
      >
        <input
          type="radio"
          class={{
            'radio__input': true,
            'radio__input--error': hasError,
          }}
          name={resolvedName}
          value={this.value}
          checked={this.checked}
          disabled={this.disabled}
          required={this.required}
          onChange={this.handleChange}
          tabIndex={this.disabled ? -1 : 0}
          onFocus={this.handleFocus}
        />
        <span class="radio__control" aria-hidden="true">
          {this.checked && <span class="radio__dot"></span>}
        </span>
        <span class="radio__label">{this.label || <slot />}</span>
        {hasError && errorText && (
          <me-text variant="small" class="form-error-message">
            {errorText}
          </me-text>
        )}
      </label>
    );
  }
}
